import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/mahjongData';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  if (relatedArticles.length === 0) {
    articles
      .filter((a) => a.slug !== article.slug)
      .slice(0, 3)
      .forEach((a) => relatedArticles.push(a));
  }

  return (
    <>
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 max-w-[1024px] mx-auto pb-16">
          {/* Header */}
          <header className="mb-8">
            <span className="inline-block px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base leading-relaxed">{article.excerpt}</p>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-[400px] rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div
            className="prose prose-foreground max-w-none
              prose-h2:font-heading prose-h2:text-xl prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-6
              prose-li:text-foreground-600 prose-li:mb-2 prose-li:leading-relaxed
              prose-strong:text-foreground-800
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
              [&_p]:text-foreground-600 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-base
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-6
              [&_li]:text-foreground-600 [&_li]:mb-2 [&_li]:leading-relaxed
              [&_strong]:text-foreground-800"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* CTA */}
          <div className="mt-12 p-8 bg-primary-50 border border-primary-200 rounded-lg text-center">
            <h2 className="font-heading text-xl text-primary-700 mb-3">准备好实战了吗？</h2>
            <p className="text-foreground-600 mb-5 max-w-md mx-auto">
              理论结合实践，立即打开麻将电脑版在线玩，将学到的技巧应用到实战中！
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/jingdian-mahjong"
                className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                经典麻将开局
              </Link>
              <Link
                to="/category/single-player"
                className="px-6 py-2 border border-primary-300 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-100 transition-all whitespace-nowrap cursor-pointer"
              >
                单机练习模式
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-secondary-50">
            <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-10">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2 py-1 text-xs bg-accent-100 text-accent-700 rounded-full mb-2">
                        {ra.category}
                      </span>
                      <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-1 line-clamp-2 leading-relaxed">{ra.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            articleBody: article.content.replace(/<[^>]*>/g, ''),
            url: `https://mahjong-online.com/article/${article.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '麻将电脑版在线玩',
              url: 'https://mahjong-online.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://mahjong-online.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}